// src/App.js
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { theme } from './theme/theme';
import { GameProvider, useGameContext } from './context/GameContext';
import { LoginScreen } from './components/LoginScreen';
import { HomeScreen } from './components/HomeScreen';
import { Lobby } from './components/Lobby';
import { GameVoiceChat } from './components/GameVoiceChat';
import { GameAudioControls } from './components/GameAudioControls';
import { playGameSound } from './audio/gameAudio';
import { Alert, Box, CircularProgress, Snackbar, Typography } from '@mui/material';
import { GAME_DEFINITIONS, GAME_TYPES, DEFAULT_GAME_TYPE, getGameStateKey, isGameStateReady } from './core/gameRegistry';

const GAME_COMPONENTS = Object.fromEntries(
  GAME_TYPES.map(type => [type, lazy(GAME_DEFINITIONS[type].loadComponent)])
);

function GameLoadingFallback({ label }) {
  return (
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--app-background)', color: 'white' }}>
      <CircularProgress sx={{ color: 'var(--app-primary)', mb: 2 }} />
      <Typography sx={{ fontWeight: 700, color: 'var(--app-text-secondary)' }}>{label}</Typography>
    </Box>
  );
}


function AppContent() {
  const { state } = useGameContext();
  const { isAuthReady, isLoggedIn, roomId, room, notification } = state;
  const previousAudioState = useRef(null);

  useEffect(() => {
    if (!roomId || !room) {
      previousAudioState.current = null;
      return;
    }

    const gameStateKey = getGameStateKey(room.gameType);
    const gameState = gameStateKey ? room[gameStateKey] || {} : {};
    const currentAudioState = {
      roomId,
      gameType: room.gameType,
      status: room.status,
      round: room.currentRound ?? gameState.currentHoleIdx ?? gameState.currentQuestionIndex,
      phase: gameState.phase,
      turn: room.currentDrawer ?? gameState.currentTurn ?? gameState.currentTurnIndex ??
        gameState.currentIndex ?? gameState.currentPlayer ?? gameState.currentPlayerId,
      dice: gameState.diceValue ?? gameState.lastRoll,
      winner: gameState.winner,
      lastAction: JSON.stringify(gameState.lastAction || null),
      activity: room.gameType === 'boggle' ? null : JSON.stringify(
        gameState.positions || gameState.pieces || gameState.balls ||
        gameState.submissions || gameState.answers ||
        gameState.discardPile?.length || gameState.turnCount || null
      ),
      scores: JSON.stringify(gameState.scores || Object.values(room.players || {}).map(player => player.score || 0)),
    };

    const previous = previousAudioState.current;
    previousAudioState.current = currentAudioState;
    if (!previous || previous.roomId !== roomId || previous.gameType !== room.gameType) return;
    if (previous.status !== currentAudioState.status) {
      if (currentAudioState.status === 'finished' || currentAudioState.winner) playGameSound('win');
      else if (currentAudioState.status === 'playing') playGameSound('start');
      else if (currentAudioState.status === 'roundEnd') playGameSound('move');
      return;
    }
    if (previous.round !== currentAudioState.round) {
      playGameSound('start');
    } else if (previous.winner !== currentAudioState.winner && currentAudioState.winner) {
      playGameSound('win');
    } else if (previous.dice !== currentAudioState.dice && currentAudioState.dice != null) {
      playGameSound('roll');
    } else if (previous.lastAction !== currentAudioState.lastAction && currentAudioState.lastAction) {
      playGameSound('card');
    } else if (previous.scores !== currentAudioState.scores) {
      playGameSound('correct');
    } else if (previous.phase !== currentAudioState.phase && currentAudioState.phase) {
      playGameSound('start');
    } else if (previous.activity !== currentAudioState.activity) {
      playGameSound(room.gameType === 'uno' ? 'card' : 'move');
    } else if (previous.turn !== currentAudioState.turn && currentAudioState.turn != null) {
      playGameSound('move');
    }
  }, [roomId, room]);

  if (!isAuthReady) {
    return (
      <Box sx={{ height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--app-background)' }}>
        <CircularProgress sx={{ color: 'var(--app-primary)' }} />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ height: '100dvh' }}>
        <LoginScreen />
      </motion.div>
    );
  }

  const screen = !roomId ? 'home'
    : !room ? 'home'
    : room.status === 'waiting' ? 'lobby'
    : 'game';

  const GameComp = GAME_COMPONENTS[room?.gameType] || GAME_COMPONENTS[DEFAULT_GAME_TYPE];

  return (
    <>
      {isLoggedIn && (
        <Box
          component="nav"
          aria-label="Voice and game audio controls"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.35,
            boxSizing: 'border-box',
            height: 'max(72px, calc(env(safe-area-inset-bottom) + 64px))',
            pb: 'env(safe-area-inset-bottom)',
            bgcolor: 'rgba(8,12,18,0.98)',
            borderTop: '1px solid var(--app-border-emphasis)',
            boxShadow: '0 -8px 28px rgba(0,0,0,0.36)',
            backdropFilter: 'blur(14px)',
          }}
        >
          <GameAudioControls docked />
          {screen === 'game' && <GameVoiceChat key={roomId} roomId={roomId} docked />}
        </Box>
      )}
      <AnimatePresence mode="wait">
        {screen === 'home' && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <HomeScreen />
          </motion.div>
        )}
        {screen === 'lobby' && (
          <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <Lobby />
          </motion.div>
        )}
        {screen === 'game' && (
          <motion.div key="game" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} style={{ height: '100dvh' }}>
            {isGameStateReady(room) ? (
              <Suspense fallback={<GameLoadingFallback label="Loading game..." />}>
                <GameComp />
              </Suspense>
            ) : (
              <GameLoadingFallback label="Loading game state..." />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={!!notification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 10002 }}
      >
        <Alert
          severity={['success', 'info', 'warning', 'error'].includes(notification?.type) ? notification.type : 'info'}
          variant="filled"
          role="status"
        >
          {notification?.msg}
        </Alert>
      </Snackbar>

      {/* Global escape hatch — floats over any game screen */}
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GameProvider>
        <AppContent />
      </GameProvider>
    </ThemeProvider>
  );
}